import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

type SeoConfig = {
  title: string
  description: string
  robots?: string
  canonicalPath?: string
}

const SITE_URL = 'https://nessakodo.com'
const DEFAULT_DESCRIPTION =
  'Creative technologist exploring sound, code, and living systems through live coding, generative sound, and interactive installations.'

const ROUTE_META: Array<{ match: RegExp; config: SeoConfig }> = [
  {
    match: /^\/$/,
    config: {
      title: 'Nessa Kodo - Creative Technologist | Sound, Code & Living Systems',
      description: DEFAULT_DESCRIPTION,
      robots: 'index, follow',
      canonicalPath: '/',
    },
  },
  {
    match: /^\/about\/?$/,
    config: {
      title: 'About Nessa Kodo | Sound, Code & Living Systems',
      description:
        'Learn about Nessa Kodo, a creative technologist working across live coding, generative sound, and interactive systems.',
      robots: 'index, follow',
      canonicalPath: '/about',
    },
  },
  {
    match: /^\/sessions\/?$/,
    config: {
      title: 'Sessions & Drops | Nessa Kodo',
      description:
        'Explore live coding sessions, drops, and generative music experiments by Nessa Kodo.',
      robots: 'index, follow',
      canonicalPath: '/sessions',
    },
  },
  {
    match: /^\/library\/?$/,
    config: {
      title: 'Library | Nessa Kodo',
      description:
        'Browse the Nessa Kodo library of Sonic Pi sessions, Strudel notebooks, Playtronica mappings, patch diagrams, and starter kits.',
      robots: 'index, follow',
      canonicalPath: '/library',
    },
  },
  {
    match: /^\/library\/(sonic-pi|strudel|playtronica|patches|stems|starter-kit)\/?$/,
    config: {
      title: 'Library Item | Nessa Kodo',
      description:
        'Detailed session notes and assets from the Nessa Kodo library.',
      robots: 'index, follow',
    },
  },
  {
    match: /^\/(community|dashboard|settings|admin)(\/.*)?$/,
    config: {
      title: 'Nessa Kodo',
      description: DEFAULT_DESCRIPTION,
      robots: 'noindex, nofollow',
    },
  },
  {
    match: /^\/(tutorial|creation|user)(\/.*)?$/,
    config: {
      title: 'Nessa Kodo',
      description: DEFAULT_DESCRIPTION,
      robots: 'noindex, nofollow',
    },
  },
]

function upsertMetaByName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertMetaByProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function resolveMeta(pathname: string): SeoConfig {
  for (const entry of ROUTE_META) {
    if (entry.match.test(pathname)) return entry.config
  }
  return {
    title: 'Nessa Kodo',
    description: DEFAULT_DESCRIPTION,
    robots: 'index, follow',
  }
}

export default function SeoRouteMeta() {
  const location = useLocation()

  useEffect(() => {
    const meta = resolveMeta(location.pathname)
    const canonicalPath = meta.canonicalPath || location.pathname
    const canonical = `${SITE_URL}${canonicalPath === '/' ? '/' : canonicalPath.replace(/\/$/, '')}`

    document.title = meta.title
    upsertMetaByName('description', meta.description)
    upsertMetaByName('robots', meta.robots || 'index, follow')
    upsertMetaByProperty('og:title', meta.title)
    upsertMetaByProperty('og:description', meta.description)
    upsertMetaByProperty('og:url', canonical)
    upsertMetaByName('twitter:title', meta.title)
    upsertMetaByName('twitter:description', meta.description)
    upsertMetaByName('twitter:url', canonical)
    upsertCanonical(canonical)
  }, [location.pathname])

  return null
}

